import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ClickAwayListener } from "@mui/material";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";

import { Button } from "@fragments";

import PresetsSidebar from "./components/PresetsSidebar";
import HeaderInputs from "./components/HeaderInputs";
import MonthGrid from "./components/MonthGrid";
import MonthHeader from "./components/MonthHeader";

import "./styles.scss";

export default function IntervalView({
    open,
    anchorEl,
    leftMonth,
    rightMonth,
    draftFrom,
    draftTo,
    presetId,
    canApply,
    presets,
    minDate,
    maxDate,
    handleDayClick,
    handleDayHover,
    handlePresetClick,
    handleFromInput,
    handleToInput,
    goPrevMonth,
    goNextMonth,
    setLeftMonthValue,
    setRightMonthValue,
    handleApply,
    handleCancel,
    handleClose,
    isInRange,
    isStart,
    isEnd,
    isDisabled,
}) {
    const portalRef = useRef(null);
    if (typeof document !== "undefined" && portalRef.current === null) {
        portalRef.current = document.createElement("div");
        portalRef.current.className = "date-picker-interval-portal";
    }

    useEffect(() => {
        const node = portalRef.current;
        if (!node || typeof document === "undefined") return undefined;
        document.body.appendChild(node);
        return () => {
            if (node.parentNode) node.parentNode.removeChild(node);
        };
    }, []);

    const [popupEl, setPopupEl] = useState(null);
    const [position, setPosition] = useState({ top: 0, left: 0, ready: false });

    useLayoutEffect(() => {
        if (!open || !anchorEl || !popupEl) {
            setPosition((p) => ({ ...p, ready: false }));
            return undefined;
        }

        function compute() {
            const a = anchorEl.getBoundingClientRect();
            const p = popupEl.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const gap = 10;
            const margin = 12;

            let top = a.bottom + gap;
            let left = a.left;

            if (top + p.height > vh - margin) {
                const flipped = a.top - p.height - gap;
                if (flipped >= margin) top = flipped;
            }
            left = Math.max(margin, Math.min(left, vw - p.width - margin));
            top = Math.max(margin, Math.min(top, vh - p.height - margin));

            setPosition({ top, left, ready: true });
        }

        compute();
        window.addEventListener("scroll", compute, true);
        window.addEventListener("resize", compute);
        return () => {
            window.removeEventListener("scroll", compute, true);
            window.removeEventListener("resize", compute);
        };
    }, [open, anchorEl, popupEl]);

    if (!open || !portalRef.current) return null;

    const popup = (
        <div
            ref={setPopupEl}
            className="date-picker-interval"
            style={{
                position: "fixed",
                top: position.top,
                left: position.left,
                visibility: position.ready ? "visible" : "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <ClickAwayListener onClickAway={handleClose}>
                <div className="date-picker-interval__paper">
                    <PresetsSidebar
                        presets={presets}
                        activeId={presetId}
                        onPick={handlePresetClick}
                    />

                    <div className="date-picker-interval__main">
                        <HeaderInputs
                            from={draftFrom}
                            to={draftTo}
                            onFromChange={handleFromInput}
                            onToChange={handleToInput}
                        />

                        <div className="date-picker-interval__calendars">
                            <button
                                type="button"
                                className="date-picker-interval__nav date-picker-interval__nav--left"
                                onClick={goPrevMonth}
                                aria-label="Mês anterior"
                            >
                                <IconChevronLeft size={18} stroke={2} />
                            </button>

                            <div className="date-picker-interval__month">
                                <MonthHeader
                                    year={leftMonth.getFullYear()}
                                    month={leftMonth.getMonth()}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    onChange={setLeftMonthValue}
                                />
                                <MonthGrid
                                    year={leftMonth.getFullYear()}
                                    month={leftMonth.getMonth()}
                                    isInRange={isInRange}
                                    isStart={isStart}
                                    isEnd={isEnd}
                                    isDisabled={isDisabled}
                                    onDayClick={handleDayClick}
                                    onDayHover={handleDayHover}
                                />
                            </div>

                            <div className="date-picker-interval__month">
                                <MonthHeader
                                    year={rightMonth.getFullYear()}
                                    month={rightMonth.getMonth()}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    onChange={setRightMonthValue}
                                />
                                <MonthGrid
                                    year={rightMonth.getFullYear()}
                                    month={rightMonth.getMonth()}
                                    isInRange={isInRange}
                                    isStart={isStart}
                                    isEnd={isEnd}
                                    isDisabled={isDisabled}
                                    onDayClick={handleDayClick}
                                    onDayHover={handleDayHover}
                                />
                            </div>

                            <button
                                type="button"
                                className="date-picker-interval__nav date-picker-interval__nav--right"
                                onClick={goNextMonth}
                                aria-label="Próximo mês"
                            >
                                <IconChevronRight size={18} stroke={2} />
                            </button>
                        </div>

                        <div className="date-picker-interval__footer">
                            <Button
                                text="Cancelar"
                                size="small"
                                variant="default"
                                color="overlay"
                                onClick={handleCancel}
                                preventLoading
                            />
                            <Button
                                text="Aplicar"
                                size="small"
                                variant="default"
                                color="primary"
                                disabled={!canApply}
                                onClick={handleApply}
                                preventLoading
                            />
                        </div>
                    </div>
                </div>
            </ClickAwayListener>
        </div>
    );

    return createPortal(popup, portalRef.current);
}
